import mergeProps from 'merge-props';
import useInternalLinks from '@/hooks/useInternalLinks';
import getDownloadURL from '@/utils/getDownloadURL';
import { Link as GatsbyLink } from 'gatsby';
import { type MouseEventHandler, useMemo, type MouseEvent, lazy, Suspense } from 'react';
import useModal from '../../Modal/hooks/useModal';
import useDatalayerEvent from '@/hooks/useDatalayerEvent';
import getInternalPageUrl from '../utils/getInternalPageUrl';
import useModalDefinition from '@/hooks/useModalProps';
import useNavattic from './useNavattic';

const ModalV2 = lazy(() => import('../../ModalV2/ModalV2'));
const Modal = lazy(() => import('../../Modal/Modal'));

declare global {
  interface Window {
    Optanon: {
      ToggleInfoDisplay: () => void;
    };
  }
}

type UseLinkPropsArgs = {
  action: Queries.LINKFragment['linkElements']['action']['value'] | null | undefined;
  target: Queries.kontent_item_link_elements_target_enum | null | undefined;
  event: Queries.DATALAYER_EVENTFragment | null | undefined;
  queryParams?: Record<string, string>;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};

/**
 * Returns the correct component (or HTML tag) and its props for the given action and target.
 * Also takes care of returning potential additional children (such as a modal)
 */
const useLinkProps = ({ action, target, event, queryParams, onClick }: UseLinkPropsArgs) => {
  const { open } = useModal();
  const getInternalLinkByCodename = useInternalLinks();
  const getModalById = useModalDefinition();
  const { triggerEvent, hasEvent } = useDatalayerEvent(event);
  useNavattic(action);

  const returnValue = useMemo(() => {
    const onClickTriggerEvent = {
      onClick: (event: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => {
        onClick && onClick(event);
        hasEvent && triggerEvent();
      },
    };

    const URLWithParameters = (url: string) => {
      if (!queryParams) {
        return url;
      }
      const urlObject = new URL(url);
      Object.entries(queryParams).forEach(([k, v]: string[]) => {
        urlObject.searchParams.set(k, v);
      });
      return urlObject.toString();
    };

    const buildProps = () => {
      if (!action) {
        return { linkChildren: null, linkProps: null };
      }

      switch (action.__typename) {
        case 'kontent_item_link_action_open_modal':
          return {
            linkChildren: (
              <Suspense>
                <Modal {...action.elements.modal.value} />
              </Suspense>
            ),
            linkProps: {
              as: 'button' as const,
              onClick: () => {
                open(action.elements.modal.value.modalElements.url_slug.value);
              },
            },
          };

        case 'kontent_item_link_action_open_modal_v2': {
          const modalProps = getModalById(
            action.elements.modal.value.system.id,
            action.elements.modal.value.preferred_language
          );

          return {
            linkChildren: (
              <Suspense>
                <ModalV2 {...modalProps} />
              </Suspense>
            ),
            linkProps: {
              as: 'button' as const,
              onClick: () => {
                open(modalProps.modalV2Elements.url_slug.value);
              },
            },
          };
        }
        case 'kontent_item_link_action_internal_page': {
          const shouldOpenNewTab = target === '_blank';
          const destination = getInternalPageUrl({
            internalPageAction: action,
            getInternalLinkByCodename,
          });

          return {
            linkChildren: null,
            linkProps: {
              ...(shouldOpenNewTab
                ? {
                    as: 'a' as const,
                    href: destination,
                  }
                : {
                    as: GatsbyLink,
                    to: destination,
                  }),
              target: target || '_self',
            },
          };
        }
        case 'kontent_item_link_action_asset': {
          const { force_download, asset, redirect_url } = action.elements;
          const isForceDownload = force_download?.value === 'yes';
          const href =
            redirect_url?.value ||
            (isForceDownload ? getDownloadURL(asset.value.url) : asset.value.url);

          return {
            linkChildren: null,
            linkProps: {
              as: 'a' as const,
              target: target || '_blank',
              href,
              ...(isForceDownload && { download: asset.value.name }),
            },
          };
        }
        case 'kontent_item_link_action_external_page':
          return {
            linkChildren: null,
            linkProps: {
              as: 'a' as const,
              target: target || '_blank',
              href: URLWithParameters(action.elements.url.value),
            },
          };
        case 'kontent_item_link_action_navattic_pop_up':
          return {
            linkChildren: null,
            linkProps: {
              as: 'button' as const,
              'data-navattic-open': action.elements.url.value,
              'data-navattic-title': action.elements.title.value,
            },
          };
        case 'kontent_item_link_action_optanon_pop_up':
          return {
            linkChildren: null,
            linkProps: {
              as: 'button' as const,
              onClick: () => {
                if (typeof window !== 'undefined' && 'Optanon' in window) {
                  window.Optanon.ToggleInfoDisplay();
                }
              },
            },
          };
        /* istanbul ignore next */
        default: {
          const exhaustiveCheck: never = action;
          throw new Error(`Unhandled action case: ${exhaustiveCheck}`);
        }
      }
    };

    const { linkProps, linkChildren } = buildProps();

    return {
      linkChildren,
      linkProps: linkProps
        ? (mergeProps(linkProps || {}, { ...onClickTriggerEvent }) as typeof linkProps)
        : null,
    };
  }, [
    onClick,
    hasEvent,
    triggerEvent,
    queryParams,
    action,
    getModalById,
    target,
    open,
    getInternalLinkByCodename,
  ]);

  return returnValue;
};

export default useLinkProps;
